import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPublicBranchDisplay, getPublicBranchScreen } from '../api/branchDisplayApi';
import BranchDisplayPlayer from '../components/branchDisplay/BranchDisplayPlayer';
import useBranchDisplayClock from '../components/branchDisplay/useBranchDisplayClock';

const BranchDisplayPage = () => {
  const navigate = useNavigate();
  const { branchId, screenId } = useParams();
  const [display, setDisplay] = useState(null);
  const [connection, setConnection] = useState('loading');
  const request = useRef({ id: 0, controller: null });
  const loadDisplay = useCallback(async () => {
    request.current.controller?.abort();
    const controller = new AbortController();
    const id = request.current.id + 1;
    request.current = { id, controller };
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const payload = (screenId ? await getPublicBranchScreen(branchId, screenId, { signal: controller.signal }) : await getPublicBranchDisplay(branchId, { signal: controller.signal }));
      if (id !== request.current.id || controller.signal.aborted) return;
      setConnection('online');
      setDisplay({ ...payload, promotionClock: payload.promotionClock ? {
        ...payload.promotionClock,
        receivedAt: payload.promotionClock.receivedAt ?? Date.now(),
      } : null });
    } catch (error) {
      if (id !== request.current.id) return;
      setConnection('offline');
      if ([400, 403, 404, 410].includes(error?.response?.status ?? error?.status)) {
        setConnection('online');
        setDisplay(null);
      }
      // A temporary network/server failure retains only this page's last successful response.
    } finally { window.clearTimeout(timeout); }
  }, [branchId, screenId]);

  useBranchDisplayClock(display, loadDisplay);

  useEffect(() => {
    setDisplay(null);
    loadDisplay();
    const refresh = window.setInterval(loadDisplay, 60000);
    const visible = () => { if (document.visibilityState === 'visible') loadDisplay(); };
    window.addEventListener('online', loadDisplay);
    document.addEventListener('visibilitychange', visible);
    return () => {
      request.current.id += 1;
      request.current.controller?.abort();
      window.clearInterval(refresh);
      window.removeEventListener('online', loadDisplay);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [loadDisplay]);

  useEffect(() => {
    const originalTitle = document.title;
    document.title = display?.branchName ? `${display.branchName} · ${display.name || 'Destacados'}` : 'Pantalla de destacados';
    return () => { document.title = originalTitle; };
  }, [display?.branchName, display?.name]);

  const currentDisplay = String(display?.branchId) === String(branchId) && String(display?.screenId || '') === String(screenId || '') ? display : null;
  return <BranchDisplayPlayer key={`${branchId}:${screenId || ''}`} display={currentDisplay} connectionStatus={connection} onChooseBranch={async () => {
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* Navigation still allows choosing another branch. */ }
    }
    navigate('/display');
  }} />;
};

export default BranchDisplayPage;
